import React, { useEffect, useState, useCallback } from "react";
import { ApiRequestNoLoder } from "../services/api";

const btn = { color: "#fff", textDecoration: "none", padding: "6px 12px", borderRadius: 6, fontSize: 13, fontWeight: 600, display: "inline-block" };
const digits = (p) => (p || "").replace(/\D/g, "").slice(-10);
const wa = (p) => `https://wa.me/91${digits(p)}`;
const tel = (p) => `tel:+91${digits(p)}`;

function PersonCard({ p, tag }) {
  return (
    <div style={{ flex: 1, minWidth: 210, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 }}>{tag}</div>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{p.name || "—"}</div>
      <div style={{ color: "#6b7280", fontSize: 13 }}>{[p.city, p.state].filter(Boolean).join(", ") || "—"}</div>
      <div style={{ color: "#374151", fontSize: 13, marginTop: 2 }}>
        {p.phone || "no phone"}{p.assigned_to ? `  · owner: ${p.assigned_to}` : ""}
        {p.biodata_available ? "  · 📄 biodata" : ""}
      </div>
      {p.phone ? (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <a href={wa(p.phone)} target="_blank" rel="noreferrer" style={{ ...btn, background: "#22c55e" }}>WhatsApp</a>
          <a href={tel(p.phone)} style={{ ...btn, background: "#3b82f6" }}>Call</a>
        </div>
      ) : null}
    </div>
  );
}

function LeadRow({ lead, highlight }) {
  return (
    <div style={{ border: highlight ? "2px solid #f59e0b" : "1px solid #e5e7eb", background: highlight ? "#fffbeb" : "#f9fafb", borderRadius: 12, padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch" }}>
        <PersonCard p={lead.liker} tag="Liked by" />
        <div style={{ alignSelf: "center", fontSize: 20 }}>{highlight ? "💛" : "→"}</div>
        <PersonCard p={lead.candidate} tag="Candidate" />
      </div>
      <div style={{ marginTop: 10, fontSize: 13, color: highlight ? "#92400e" : "#374151", fontWeight: highlight ? 700 : 500 }}>
        👉 {lead.action}
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: "#9ca3af" }}>
        liked {lead.liked_at}{lead.reason ? `  · reason: ${lead.reason}` : ""}
      </div>
    </div>
  );
}

function Stat({ n, label, color }) {
  return (
    <div style={{ flex: 1, background: "#fff", border: "1px solid #e5e7eb", borderTop: `4px solid ${color}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 30, fontWeight: 800, color }}>{n}</div>
      <div style={{ fontSize: 13, color: "#6b7280" }}>{label}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 10 }}>{title}</h2>
      {children}
    </div>
  );
}

const Empty = ({ text }) => <div style={{ color: "#9ca3af", fontStyle: "italic", padding: "8px 0" }}>{text}</div>;

export default function HotLeads() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await ApiRequestNoLoder("GET", "admin/hot-leads?limit=100");
      const data = res && res.data ? res.data : res;
      if (data && data.counts) setBoard(data);
      else setErr(typeof res === "string" ? res : (res && res.message) || "Failed to load hot leads");
    } catch (e) {
      setErr((e && e.message) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>{"🔥 Hot Leads"}</h1>
        <button onClick={load} style={{ ...btn, background: "#111827", cursor: "pointer", border: "none" }}>Refresh</button>
      </div>
      <p style={{ color: "#6b7280", marginTop: 0 }}>
        Members who liked a match in the last 21 days. Work the mutual-yeses first — both families already said yes; just connect them.
      </p>

      {loading ? <div>Loading…</div> : null}
      {err ? <div style={{ color: "#dc2626" }}>{err}</div> : null}

      {board ? (
        <>
          <div style={{ display: "flex", gap: 12, margin: "12px 0 20px", flexWrap: "wrap" }}>
            <Stat n={board.counts.mutual_yes} label="Mutual yes — introduce now" color="#f59e0b" />
            <Stat n={board.counts.one_sided_likes} label="One-sided likes — chase" color="#3b82f6" />
            <Stat n={board.counts.contact_requests} label="Contact requests" color="#8b5cf6" />
          </div>

          <Section title={`💛 Both said yes — introduce now (${board.mutual_yes.length})`}>
            {board.mutual_yes.length === 0 ? <Empty text="No mutual yeses right now." /> :
              board.mutual_yes.map((l, i) => <LeadRow key={i} lead={l} highlight />)}
          </Section>

          <Section title={`👍 One-sided likes — chase the other side (${board.one_sided_likes.length})`}>
            {board.one_sided_likes.length === 0 ? <Empty text="No pending likes." /> :
              board.one_sided_likes.map((l, i) => <LeadRow key={i} lead={l} />)}
          </Section>

          <Section title={`📞 Contact requests (${board.contact_requests.length})`}>
            {board.contact_requests.length === 0 ? <Empty text="No contact requests." /> :
              board.contact_requests.map((c, i) => (
                <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, marginBottom: 8 }}>
                  <b>{c.member_phone}</b> wants contact of <b>{c.wants_contact_of}</b>
                  {c.assigned_to ? `  · assigned: ${c.assigned_to}` : ""}
                  {"  · "}{c.read ? "read" : c.delivered ? "delivered" : "sent"}{"  · "}{c.at}
                </div>
              ))}
          </Section>
        </>
      ) : null}
    </div>
  );
}
